import { useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { toast } from 'sonner';
import type { IFacilityProcessMapping } from '@/api/constants-mapping/types';
import { DeleteConfirmDialog } from '@/app/(client)/company-settings/-components/constantsMapping/DeleteConfirmDialog';
import { FacilityProcessCreateDialog } from '@/app/(client)/company-settings/-components/constantsMapping/FacilityProcessCreateDialog';
import {
  facilityProcessMappingKeys,
  useCreateFacilityProcessMappings,
  useDeleteFacilityProcessMappings,
  useFacilityProcessMappings,
  usePatchFacilityProcessMapping,
} from '@/app/(client)/company-settings/-components/constantsMapping/hooks/useFacilityProcessMappings';
import { MappingTable } from '@/app/(client)/company-settings/-components/constantsMapping/MappingTable';
import type { ColumnDef } from '@/components/shared/DataTable/tanstack';
import type { FilterDef } from '@/components/shared/DataTable/types';
import { TextValue, UpdatedAtValue } from '@/components/shared/ValueRenderers';
import { EmptyState } from '@/components/ui/empty-state';
import { useFacilityProcesses } from '@/hooks/useFacilityProcesses';
import { usePermissions } from '@/hooks/usePermissions';
import { useTable } from '@/hooks/useTable';
import { useTranslator } from '@/hooks/useTranslator';

export const Route = createFileRoute(
  '/(client)/company-settings/(pages)/constants-mapping/facility-processes/',
)({
  component: FacilityProcessesPage,
});

function FacilityProcessesPage() {
  const { getTranslation: t } = useTranslator({ page: 'constantsMapping' });
  const queryClient = useQueryClient();
  const table = useTable('constants-mapping-facility-processes');
  const perms = usePermissions()['constant-mapping'];

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);

  const { data, isFetching, isError } = useFacilityProcessMappings({
    page: table.page,
    limit: table.limit,
    ...(table.queryParams.sort !== undefined
      ? { sort: table.queryParams.sort }
      : {}),
    ...(table.queryParams.filter !== undefined
      ? { filter: table.queryParams.filter }
      : {}),
  });
  const { processes } = useFacilityProcesses();
  const patchMutation = usePatchFacilityProcessMapping();
  const deleteMutation = useDeleteFacilityProcessMappings();
  const createMutation = useCreateFacilityProcessMappings();

  if (isError)
    return (
      <EmptyState
        title={t('error.title')}
        description={t('error.description')}
      />
    );

  const columns: ColumnDef<IFacilityProcessMapping>[] = [
    {
      id: 'name',
      accessorKey: 'name',
      header: t('facilityProcess.columns.retracedName'),
      enableSorting: true,
      cell: ({ getValue }) => (
        <TextValue
          value={getValue<string | null>()}
          emptyNode={<span className='text-xs text-muted-foreground'>—</span>}
        />
      ),
    },
    {
      id: 'productionProcessName',
      accessorKey: 'productionProcessName',
      header: t('facilityProcess.columns.productionProcess'),
      enableSorting: true,
      cell: ({ getValue }) => (
        <TextValue
          value={getValue<string | null>()}
          emptyNode={<span className='text-xs text-muted-foreground'>—</span>}
        />
      ),
    },
    {
      id: 'retracedFacilityProcessId',
      accessorKey: 'retracedFacilityProcessId',
      header: t('columns.retracedId'),
      enableSorting: true,
      cell: ({ getValue }) => (
        <TextValue
          value={getValue<string | null>()}
          emptyNode={<span className='text-xs text-muted-foreground'>—</span>}
        />
      ),
    },
    {
      id: 'externalName',
      accessorKey: 'externalName',
      header: t('facilityProcess.columns.yourName'),
      enableSorting: true,
      meta: {
        editor: 'text',
        editKey: 'externalName',
      },
      cell: ({ getValue }) => <TextValue value={getValue<string | null>()} />,
    },
    {
      id: 'externalId',
      accessorKey: 'externalId',
      header: t('facilityProcess.columns.constantId'),
      enableSorting: true,
      meta: {
        editor: 'text',
        editKey: 'externalId',
      },
      cell: ({ getValue }) => <TextValue value={getValue<string | null>()} />,
    },
    {
      id: 'updatedAt',
      accessorKey: 'updatedAt',
      header: t('columns.updatedAt'),
      enableSorting: true,
      cell: ({ row }) => (
        <UpdatedAtValue
          value={row.original.updatedAt}
          userName={row.original.updatedByUserName ?? null}
          companyName={row.original.updatedByCompanyName ?? null}
        />
      ),
    },
  ];

  const filterDefs: FilterDef<IFacilityProcessMapping>[] = [
    {
      id: 'retracedFacilityProcessId',
      label: t('facilityProcess.columns.retracedName'),
      type: 'select',
      options: processes,
    },
    {
      id: 'externalName',
      label: t('facilityProcess.columns.yourName'),
      type: 'text',
    },
    {
      id: 'externalId',
      label: t('facilityProcess.columns.constantId'),
      type: 'text',
    },
  ];

  const handleCellCommit = async (
    _rowIndex: number,
    colId: string,
    rawValue: unknown,
    row: IFacilityProcessMapping,
  ) => {
    try {
      await patchMutation.mutateAsync({
        id: row.id,
        body: {
          externalName:
            colId === 'externalName'
              ? String(rawValue ?? '')
              : (row.externalName ?? ''),
          externalId:
            colId === 'externalId'
              ? String(rawValue ?? '')
              : (row.externalId ?? ''),
        },
      });
      void queryClient.invalidateQueries({
        queryKey: facilityProcessMappingKeys.all(),
      });
    } catch {
      toast.error(t('toast.saveError'));
      throw new Error('patch failed');
    }
  };

  const handleDelete = async () => {
    try {
      await deleteMutation.mutateAsync([...selectedIds]);
      void queryClient.invalidateQueries({
        queryKey: facilityProcessMappingKeys.all(),
      });
      toast.success(
        t('toast.deleteSuccess', { count: String(selectedIds.size) }),
      );
      setSelectedIds(new Set());
      setDeleteOpen(false);
    } catch {
      toast.error(t('toast.deleteError'));
    }
  };

  const handleCreate = async (
    rows: {
      retracedFacilityProcessId: string;
      externalId: string;
      externalName: string;
    }[],
  ) => {
    try {
      await createMutation.mutateAsync(rows);
      void queryClient.invalidateQueries({
        queryKey: facilityProcessMappingKeys.all(),
      });
      toast.success(t('toast.createSuccess'));
      setCreateOpen(false);
    } catch {
      toast.error(t('toast.createError'));
    }
  };

  return (
    <>
      <MappingTable
        columns={columns}
        filterDefs={filterDefs}
        data={data?.data ?? []}
        isFetching={isFetching}
        totalCount={data?.metadata.pagination.total ?? 0}
        table={table}
        storageKey='constants-mapping-facility-processes'
        selectedIds={selectedIds}
        onSelectionChange={setSelectedIds}
        rowIdKey='id'
        onAdd={() => setCreateOpen(true)}
        onDelete={() => setDeleteOpen(true)}
        isDeleting={deleteMutation.isPending}
        emptyTitle={t('facilityProcess.empty.title')}
        emptyDescription={t('facilityProcess.empty.description')}
        onCellCommit={handleCellCommit}
        canCreate={perms.canCreate}
        canEdit={perms.canEdit}
        canDelete={perms.canDelete}
      />
      <DeleteConfirmDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        onConfirm={handleDelete}
        count={selectedIds.size}
      />
      <FacilityProcessCreateDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        processes={processes}
        onConfirm={handleCreate}
        isLoading={createMutation.isPending}
      />
    </>
  );
}
